import {
  addKeyframe,
  evaluateParam,
  findClip,
  formatTimecode,
  framesToSeconds,
  moveClips,
  paramsForClipType,
  removeKeyframes,
  removeMarker,
  resetParam,
  setParam,
  setParamAnimated,
  trimClip,
  updateClip,
  updateKeyframe,
  updateTransition,
  type BlendMode,
  type Clip,
  type Easing,
  type FitMode,
  type ParamDefinition,
  type Id,
  type Project,
  type TextClip,
  type TransitionType,
} from '@ve/core';
import { ChevronLeft, ChevronRight, Diamond, ExternalLink, RotateCcw, Timer, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { NumberField } from '../../components/NumberField.tsx';
import { edit, gesture, useEditor } from '../../state/editor-store.ts';
import { transport, usePlayback } from '../../state/playback.ts';
import { useUi } from '../../state/ui-store.ts';
import { tip } from '../../lib/tooltip.ts';
import { PARAM_HELP } from '../../lib/labels.ts';
import { deleteSelection, setClipsMuted, shortcutOf, soundClips } from '../../lib/commands.ts';
import { TRANSITION_LABELS } from '../../lib/labels.ts';

export function Inspector() {
  const project = useEditor((s) => s.project)!;
  const selection = useEditor((s) => s.selection);

  let body: ReactNode;
  if (selection.transitionId) body = <TransitionInspector project={project} id={selection.transitionId} />;
  else if (selection.clipIds.length === 1) {
    const loc = findClip(project, selection.clipIds[0]!);
    body = loc ? <ClipInspector project={project} clip={loc.clip} locked={loc.track.locked} /> : null;
  } else if (selection.clipIds.length > 1) {
    body = <MultiClipInspector project={project} ids={selection.clipIds} />;
  } else body = <SequenceInspector project={project} />;

  return (
    <section className="panel inspector">
      <div className="panel-header">Inspector</div>
      <div className="panel-body">{body}</div>
    </section>
  );
}

function Section({ title, children, actions }: { title: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="insp-section">
      <div className="insp-section-title">
        {title}
        <span className="spacer" />
        {actions}
      </div>
      <div className="insp-section-body">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="insp-field">
      <label>{label}</label>
      <div className="insp-control">{children}</div>
    </div>
  );
}

/** Settings that apply to every selected clip at once. */
function MultiClipInspector({ project, ids }: { project: Project; ids: Id[] }) {
  const sound = soundClips(project, ids);
  const muted = sound.filter((c) => c.muted).length;
  return (
    <div>
      <div className="insp-title">{ids.length} clips selected</div>
      <Section title="Audio">
        {sound.length > 0 ? (
          <Field label="Mute">
            <input
              type="checkbox"
              checked={muted === sound.length}
              // Mixed: some selected clips are muted, some aren't.
              ref={(el) => {
                if (el) el.indeterminate = muted > 0 && muted < sound.length;
              }}
              onChange={(e) =>
                setClipsMuted(
                  sound.map((c) => c.id),
                  e.target.checked,
                )
              }
              {...tip(`Mute or unmute all ${sound.length} clips with sound`, shortcutOf('toggleMuted'))}
            />
          </Field>
        ) : (
          <div className="muted">None of the selected clips have sound.</div>
        )}
      </Section>
      <div className="insp-hint muted">
        Shift- or {navigator.platform.startsWith('Mac') ? '⌘' : 'Ctrl'}-click clips, or drag a box around
        them, to change the selection.
      </div>
    </div>
  );
}

const CLIP_TYPE_LABEL: Record<Clip['type'], string> = {
  video: 'Video',
  image: 'Image',
  audio: 'Audio',
  text: 'Text',
  solid: 'Color solid',
  sequence: 'Chapter',
};

function ClipInspector({ project, clip, locked }: { project: Project; clip: Clip; locked: boolean }) {
  const fps = project.settings.fps;
  const groups: Array<{ id: ParamDefinition['group']; title: string }> = [
    { id: 'transform', title: 'Transform' },
    { id: 'compositing', title: 'Compositing' },
    { id: 'crop', title: 'Crop' },
    { id: 'audio', title: 'Audio' },
  ];
  const defs = paramsForClipType(clip.type);
  const patch = (label: string, p: Parameters<typeof updateClip>[2]) =>
    edit(label, (d) => updateClip(d, clip.id, p));

  return (
    <div className={locked ? 'insp-locked' : undefined}>
      <div className="insp-title">
        <span className={`type-dot clip-${clip.type}`} />
        <input
          className="input insp-name"
          value={clip.name}
          onChange={(e) => patch('Rename clip', { name: e.target.value })}
          onFocus={() => gesture.begin('Rename clip')}
          onBlur={() => gesture.commit()}
        />
        <span className="muted">{CLIP_TYPE_LABEL[clip.type]}</span>
      </div>
      {locked && <div className="insp-note">This clip is on a locked track.</div>}

      <Section title="Timing">
        <Field label="Start">
          <NumberField
            value={clip.start}
            step={1}
            min={0}
            precision={0}
            unit="f"
            hint={`Starts at ${formatTimecode(clip.start, fps)}`}
            onGestureStart={() => gesture.begin('Move clip')}
            onGestureEnd={() => gesture.commit()}
            onChange={(v) =>
              gesture.replace('Move clip', (p) => {
                const loc = findClip(p, clip.id)!;
                moveClips(p, [{ clipId: clip.id, trackId: loc.track.id, start: Math.round(v) }]);
              })
            }
          />
        </Field>
        <Field label="Duration">
          <NumberField
            value={clip.duration}
            step={1}
            min={1}
            precision={0}
            unit="f"
            hint={`${framesToSeconds(clip.duration, fps).toFixed(2)} seconds`}
            onGestureStart={() => gesture.begin('Change duration')}
            onGestureEnd={() => gesture.commit()}
            onChange={(v) =>
              gesture.replace('Change duration', (p) =>
                trimClip(p, clip.id, 'end', clip.start + Math.round(v)),
              )
            }
          />
        </Field>
        {(clip.type === 'video' || clip.type === 'audio') && (
          <>
            <Field label="Speed">
              <NumberField
                value={clip.speed}
                step={0.01}
                min={0.05}
                max={16}
                displayScale={100}
                unit="%"
                onChange={(v) => patch('Change speed', { speed: v })}
              />
            </Field>
            <Field label="Source in">
              <span className="muted mono">{clip.sourceIn.toFixed(3)} s</span>
            </Field>
          </>
        )}
        <Field label="Enabled">
          <input
            type="checkbox"
            checked={clip.enabled}
            onChange={(e) => patch('Toggle clip', { enabled: e.target.checked })}
          />
        </Field>
      </Section>

      {clip.type === 'sequence' && (
        <Section title="Chapter">
          <button
            className="btn"
            onClick={() => useEditor.getState().openSequence(clip.sequenceId)}
            {...tip('Open the chapter in its own timeline tab to edit its contents')}
          >
            <ExternalLink size={13} /> Open “{project.sequences[clip.sequenceId]?.name}”
          </button>
        </Section>
      )}

      {clip.type === 'text' && <TextSection clip={clip} />}

      {clip.type === 'solid' && (
        <Section title="Color">
          <Field label="Fill">
            <input
              type="color"
              className="color-input"
              value={clip.color.slice(0, 7)}
              onChange={(e) => patch('Solid color', { color: e.target.value })}
            />
          </Field>
        </Section>
      )}

      {'fit' in clip && (
        <Section title="Layout">
          <Field label="Fit">
            <select
              className="select"
              value={clip.fit}
              onChange={(e) => patch('Fit mode', { fit: e.target.value as FitMode })}
            >
              <option value="contain">Fit (letterbox)</option>
              <option value="cover">Fill (crop)</option>
              <option value="stretch">Stretch</option>
              <option value="none">Native size</option>
            </select>
          </Field>
          <Field label="Blend">
            <select
              className="select"
              value={clip.blendMode}
              onChange={(e) => patch('Blend mode', { blendMode: e.target.value as BlendMode })}
            >
              <option value="normal">Normal</option>
              <option value="add">Add</option>
              <option value="multiply">Multiply</option>
              <option value="screen">Screen</option>
            </select>
          </Field>
        </Section>
      )}

      {groups.map((g) => {
        const list = defs.filter((d) => d.group === g.id);
        if (list.length === 0) return null;
        return (
          <Section key={g.id} title={g.title}>
            {g.id === 'audio' && 'muted' in clip && (
              <Field label="Mute">
                <input
                  type="checkbox"
                  checked={clip.muted}
                  onChange={(e) => patch('Mute clip', { muted: e.target.checked })}
                />
              </Field>
            )}
            {list.map((def) => (
              <ParamRow key={def.key} clip={clip} def={def} />
            ))}
          </Section>
        );
      })}
    </div>
  );
}

function ParamRow({ clip, def }: { clip: Clip; def: ParamDefinition }) {
  const frame = usePlayback((s) => s.frame);
  const rel = Math.max(0, Math.min(clip.duration - 1, frame - clip.start));
  const anim = clip.params[def.key];
  const animated = !!anim && anim.keyframes.length > 0;
  const value = evaluateParam(clip, def.key, rel);
  const kfHere = anim?.keyframes.find((k) => k.frame === rel);
  const prev = anim ? [...anim.keyframes].reverse().find((k) => k.frame < rel) : undefined;
  const next = anim?.keyframes.find((k) => k.frame > rel);
  const label = `Change ${def.label.toLowerCase()}`;

  return (
    <div className={`param-row${animated ? ' animated' : ''}`}>
      <button
        className={`btn ghost small icon stopwatch${animated ? ' on' : ''}`}
        {...tip(
          animated
            ? `Stop animating ${def.label.toLowerCase()} (removes keyframes, keeps the current value)`
            : `Animate ${def.label.toLowerCase()} with keyframes`,
        )}
        onClick={() =>
          edit(animated ? 'Remove animation' : 'Animate', (p) =>
            setParamAnimated(p, clip.id, def.key, !animated, rel),
          )
        }
      >
        <Timer size={12} />
      </button>
      <label {...tip(PARAM_HELP[def.key])}>{def.label}</label>
      <NumberField
        value={value}
        step={def.step}
        min={def.min}
        max={def.max}
        displayScale={def.displayScale}
        unit={def.unit}
        onGestureStart={() => gesture.begin(label)}
        onGestureEnd={() => gesture.commit()}
        onChange={(v) => gesture.replace(label, (p) => setParam(p, clip.id, def.key, v, rel))}
      />
      <div className="kf-controls">
        <button
          className="btn ghost small icon"
          disabled={!prev}
          {...tip(prev ? 'Jump to previous keyframe' : 'No earlier keyframe')}
          onClick={() => prev && transport.seek(clip.start + prev.frame)}
        >
          <ChevronLeft size={12} />
        </button>
        <button
          className={`btn ghost small icon kf-toggle${kfHere ? ' on' : ''}`}
          {...tip(kfHere ? 'Remove the keyframe at the playhead' : 'Add a keyframe at the playhead')}
          onClick={() =>
            kfHere
              ? edit('Remove keyframe', (p) => removeKeyframes(p, clip.id, [kfHere.id]))
              : edit('Add keyframe', (p) => {
                  if (!animated) setParamAnimated(p, clip.id, def.key, true, rel);
                  else addKeyframe(p, clip.id, def.key, rel);
                })
          }
        >
          <Diamond size={11} />
        </button>
        <button
          className="btn ghost small icon"
          disabled={!next}
          {...tip(next ? 'Jump to next keyframe' : 'No later keyframe')}
          onClick={() => next && transport.seek(clip.start + next.frame)}
        >
          <ChevronRight size={12} />
        </button>
      </div>
      {kfHere ? (
        <select
          className="select easing-select"
          value={kfHere.easing}
          {...tip('How the value moves from this keyframe to the next')}
          onChange={(e) =>
            edit('Keyframe easing', (p) =>
              updateKeyframe(p, clip.id, kfHere.id, { easing: e.target.value as Easing }),
            )
          }
        >
          <option value="linear">Linear</option>
          <option value="easeIn">Ease in</option>
          <option value="easeOut">Ease out</option>
          <option value="easeInOut">Ease in/out</option>
          <option value="hold">Hold</option>
        </select>
      ) : (
        <button
          className="btn ghost small icon reset"
          {...tip(anim ? `Reset ${def.label.toLowerCase()} to default` : 'Already at default')}
          disabled={!anim}
          onClick={() => edit(`Reset ${def.label.toLowerCase()}`, (p) => resetParam(p, clip.id, def.key))}
        >
          <RotateCcw size={11} />
        </button>
      )}
    </div>
  );
}

const FONTS = [
  'Inter, Helvetica, Arial, sans-serif',
  'Georgia, serif',
  '"Times New Roman", serif',
  '"Courier New", monospace',
  'Impact, sans-serif',
  '"Trebuchet MS", sans-serif',
  'Verdana, sans-serif',
];

function TextSection({ clip }: { clip: TextClip }) {
  const s = clip.style;
  const style = (label: string, patch: Partial<TextClip['style']>) =>
    edit(label, (p) => updateClip(p, clip.id, { style: patch }));
  return (
    <Section title="Text">
      <textarea
        className="textarea"
        rows={3}
        value={clip.text}
        onFocus={() => gesture.begin('Edit text')}
        onBlur={() => gesture.commit()}
        onChange={(e) =>
          edit('Edit text', (p) =>
            updateClip(p, clip.id, { text: e.target.value, name: e.target.value.split('\n')[0] }),
          )
        }
      />
      <Field label="Font">
        <select
          className="select"
          value={s.fontFamily}
          onChange={(e) => style('Font', { fontFamily: e.target.value })}
        >
          {[...new Set([s.fontFamily, ...FONTS])].map((f) => (
            <option key={f} value={f}>
              {f.split(',')[0]!.replace(/"/g, '')}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Size">
        <NumberField
          value={s.fontSize}
          step={1}
          min={4}
          max={1000}
          unit="px"
          onGestureStart={() => gesture.begin('Font size')}
          onGestureEnd={() => gesture.commit()}
          onChange={(v) =>
            gesture.replace('Font size', (p) => updateClip(p, clip.id, { style: { fontSize: v } }))
          }
        />
      </Field>
      <Field label="Weight">
        <select
          className="select"
          value={s.fontWeight}
          onChange={(e) => style('Font weight', { fontWeight: Number(e.target.value) })}
        >
          {[300, 400, 500, 600, 700, 800, 900].map((w) => (
            <option key={w} value={w}>
              {w}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Style">
        <div className="row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={s.italic}
              onChange={(e) => style('Italic', { italic: e.target.checked })}
            />{' '}
            Italic
          </label>
          <select
            className="select"
            value={s.align}
            onChange={(e) => style('Alignment', { align: e.target.value as TextClip['style']['align'] })}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
      </Field>
      <Field label="Color">
        <input
          type="color"
          className="color-input"
          value={s.color.slice(0, 7)}
          onChange={(e) => style('Text color', { color: e.target.value })}
        />
      </Field>
      <Field label="Outline">
        <div className="row">
          <input
            type="color"
            className="color-input"
            value={s.strokeColor.slice(0, 7)}
            onChange={(e) => style('Outline color', { strokeColor: e.target.value })}
          />
          <NumberField
            value={s.strokeWidth}
            step={0.5}
            min={0}
            max={50}
            unit="px"
            onChange={(v) => style('Outline width', { strokeWidth: v })}
          />
        </div>
      </Field>
      <Field label="Shadow">
        <div className="row">
          <input
            type="color"
            className="color-input"
            value={s.shadowColor.slice(0, 7)}
            onChange={(e) =>
              style('Shadow color', { shadowColor: `${e.target.value}${s.shadowColor.slice(7) || 'aa'}` })
            }
          />
          <NumberField
            value={s.shadowBlur}
            step={1}
            min={0}
            max={100}
            unit="px"
            onChange={(v) => style('Shadow blur', { shadowBlur: v })}
          />
        </div>
      </Field>
      <Field label="Background">
        <div className="row">
          <input
            type="checkbox"
            checked={!!s.backgroundColor}
            onChange={(e) =>
              style('Text background', { backgroundColor: e.target.checked ? '#000000aa' : undefined })
            }
          />
          {s.backgroundColor && (
            <input
              type="color"
              className="color-input"
              value={s.backgroundColor.slice(0, 7)}
              onChange={(e) =>
                style('Background color', {
                  backgroundColor: `${e.target.value}${s.backgroundColor?.slice(7) || 'aa'}`,
                })
              }
            />
          )}
          <NumberField
            value={s.padding}
            step={1}
            min={0}
            max={400}
            unit="px"
            onChange={(v) => style('Text padding', { padding: v })}
          />
        </div>
      </Field>
    </Section>
  );
}

function TransitionInspector({ project, id }: { project: Project; id: string }) {
  let found: { type: TransitionType; duration: number } | null = null;
  for (const seq of Object.values(project.sequences)) {
    for (const t of [...seq.videoTracks, ...seq.audioTracks]) {
      const tr = t.transitions.find((x) => x.id === id);
      if (tr) found = tr;
    }
  }
  if (!found) return null;
  const fps = project.settings.fps;
  return (
    <div>
      <div className="insp-title">
        <span className="type-dot transition" /> Transition
      </div>
      <Section
        title="Settings"
        actions={
          <button
            className="btn ghost small icon"
            {...tip('Delete transition', 'Delete')}
            onClick={() => deleteSelection(false)}
          >
            <Trash2 size={12} />
          </button>
        }
      >
        <Field label="Type">
          <select
            className="select"
            value={found.type}
            onChange={(e) =>
              edit('Transition type', (p) =>
                updateTransition(p, id, { type: e.target.value as TransitionType }),
              )
            }
          >
            {(Object.keys(TRANSITION_LABELS) as TransitionType[]).map((t) => (
              <option key={t} value={t}>
                {TRANSITION_LABELS[t]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Duration">
          <NumberField
            value={found.duration}
            step={1}
            min={1}
            precision={0}
            unit="f"
            hint={`${framesToSeconds(found.duration, fps).toFixed(2)} seconds, centred on the cut`}
            onGestureStart={() => gesture.begin('Transition duration')}
            onGestureEnd={() => gesture.commit()}
            onChange={(v) =>
              gesture.replace('Transition duration', (p) => updateTransition(p, id, { duration: v }))
            }
          />
        </Field>
      </Section>
    </div>
  );
}

function SequenceInspector({ project }: { project: Project }) {
  const seqId = useEditor((s) => s.activeSequenceId) ?? project.rootSequenceId;
  const seq = project.sequences[seqId]!;
  const s = project.settings;
  return (
    <div>
      <div className="insp-title">{seq.name}</div>
      <Section title="Project">
        <Field label="Resolution">
          <span className="mono">
            {s.width} × {s.height}
          </span>
        </Field>
        <Field label="Frame rate">
          <span className="mono">{(s.fps.num / s.fps.den).toFixed(3).replace(/\.?0+$/, '')} fps</span>
        </Field>
        <Field label="">
          <button
            className="btn"
            onClick={() => useUi.getState().setDialog('settings')}
            {...tip('Change resolution, frame rate and background')}
          >
            Project settings…
          </button>
        </Field>
      </Section>
      <Section title="Markers">
        {seq.markers.length === 0 && <div className="muted">Press M to add a marker at the playhead.</div>}
        {seq.markers.map((m) => (
          <div key={m.id} className="marker-row">
            <span className="marker-dot" style={{ background: m.color }} />
            <button
              className="btn ghost small mono"
              onClick={() => transport.seek(m.frame)}
              {...tip(`Jump to ${m.label || 'marker'}`)}
            >
              {formatTimecode(m.frame, s.fps)}
            </button>
            <span className="spacer" />
            <button
              className="btn ghost small icon"
              onClick={() => edit('Remove marker', (p) => removeMarker(p, seq.id, m.id))}
              {...tip('Remove marker')}
            >
              <Trash2 size={11} />
            </button>
          </div>
        ))}
      </Section>
      <div className="insp-hint muted">Select a clip to edit its properties and keyframes.</div>
    </div>
  );
}
